import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';   

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 4rem;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled(Link)`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  overflow-wrap: anywhere;
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const CardTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #333;
`;

const CardDescription = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  color: #666;
  flex-grow: 1;
`;

const CardIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: #4A90E2;
`;

const AboutPage: React.FC = () => {
  // Define structured data for About page
  const aboutStructuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About prest0",
    "description": "Learn about prest0, our mission, vision, and the team of experts behind our innovative AI platform.",
    "url": "https://prest0.ai/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "prest0",
      "description": "prest0 is revolutionizing the way people interact with AI, providing tools to create, teach, and monetize powerful AI agents.",
      "url": "https://prest0.ai",
      "logo": "https://www.prest0.ai/images/prest0-logo.webp"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://prest0.ai/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "About",
          "item": "https://prest0.ai/about"
        }
      ]
    }
  };

  return (
    <PageContainer>
      <SeoHead
        title="About prest0 | AI Employees for Law & Finance"
        tabTitle="about"
        description="Learn about prest0 — the platform building 24/7 AI employees for law and finance teams."
        keywords="about prest0, AI employees, law, finance"
        canonicalUrl="https://prest0.ai/about"
        structuredData={aboutStructuredData}
      />
      
      <HeaderSection>
        <Title>About prest0</Title>
        <Subtitle>
          prest0 is revolutionizing the way people interact with AI. Learn more about our mission, vision, and the team behind this innovative platform.
        </Subtitle>
      </HeaderSection>
      
      <CategoriesGrid>
        <CategoryCard to="/about/mission/project-mission">
          <CardIcon>🚀</CardIcon>
          <CardTitle>Our Mission</CardTitle>
          <CardDescription>
            Discover our mission to democratize AI and empower users to create, customize, and monetize powerful AI agents with ease.
          </CardDescription>
        </CategoryCard>
        
        <CategoryCard to="/team">
          <CardIcon>👥</CardIcon>
          <CardTitle>Team Expertise</CardTitle>
          <CardDescription>
            Meet our diverse team of experts who bring together experience in AI, blockchain, product development, and more.
          </CardDescription>
        </CategoryCard>
      </CategoriesGrid>
    </PageContainer>
  );
};

export default AboutPage; 
